import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { LineType } from '../models/enums/LineType';
import { use3DGridStore } from '../stores/use3DGridStore';
import { use3DSceneStore } from '../stores/use3DSceneStore';
import { use3DViewerStore } from '../stores/use3DViewerStore';
import { getUrlParams } from '../utils/Seismic3DUrlUtils';
import { getDeeperRelativeLinesToLoad, getRealLineValue, getRelativeLinesToLoadInFirstIteration, getThreeJsYFactor } from '../utils/TileUtils';
import { VolumeType } from '../models/enums/VolumeType';
import { Line2D } from './Line2D';
import { getCountTimeDepth } from '../threejs/utils/ScaleUtils';
import { Lines3D2 } from './Lines3D2';
import { Zslice3D } from './Zslice3D';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';
import { use3DFeatureFlags } from '../hooks/useSeismic3DFeatureFlags';
import { SampleIntervalUnit } from '../models/enums/SampleIntervalUnit';
import { MeasurementType } from 'features/seismic/models/enums/MeasurementType';
import { Seismic3DData } from '../models/classes/Seismic3DData';
import { ISurveyMetadata } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { GridSlider } from './Slider/GridSlider';
import { Metadata3D } from '../models/types/Metadata3D';
import { useGetSurveyGeometry } from 'features/seismic/components/worldMap/api/useWorldMapController';
import { metadata } from 'features/seismic/components/seismicVolumeController';
import { buildMeshByGeomWKT } from './projections/featureGeomPolygon';

export type Grid3DProps = {
    volumeMetadata: ISurveyMetadata,
    onLoadStart: () => void,
    onLoadEnd: () => void,
    isSliderVisible: boolean,
    onLoadingProgressChange: (progress: number) => void
    volumeIdentifier: string
};

export const Grid3D = ({ onLoadingProgressChange, volumeIdentifier, volumeMetadata, onLoadEnd, onLoadStart, isSliderVisible } : Grid3DProps) => {
    const geopostScene = use3DSceneStore(state => state.scene);
    const mainFeatureSrid = use3DViewerStore(state => state.mainFeatureSrid);
    const mainFeatureCentroid = use3DViewerStore(state => [ state.featureCentroidX, state.featureCentroidY ]);

    const currentDeepeningIterationRef = useRef(-1);

    /*
    totalZsliceNumbers,
        setSearchedInlineNumber,
        setSearchedXlineNumber,
        setSearchedZsliceNumber,
        setTotalInlineNumbers,
        setTotalXlineNumbers,
        setTotalZsliceNumbers,
        setSelectedInlineNumber,
        setSelectedXlineNumber,
        setSelectedSeismic3DInfo,
        setIsXLineSearchLoading,
        setIsInlineSearchLoading,
    */

    const [ searchedInlineNumber, setSearchedInlineNumber ] = useState<number|null>(null);
    const [ searchedXlineNumber, setSearchedXlineNumber ] = useState<number|null>(null);
    const [ searchedZSliceNumber, setSearchedZSliceNumber ] = useState<number|null>(null);

    const [ totalInlineNumbers, setTotalInlineNumbers ] = useState<number[]>([]);
    const [ totalXlineNumbers, setTotalXlineNumbers ] = useState<number[]>([]);
    const [ totalZSliceNumbers, setTotalZSliceNumbers ] = useState<number[]>([]);

    const [ selectedInlineNumber, setSelectedInlineNumber ] = useState<number>(0);
    const [ selectedXlineNumber, setSelectedXlineNumber ] = useState<number>(0);
    const [ selectedZSliceNumber, setSelectedZSliceNumber ] = useState<number>(0);

    const [ isInlineSearchLoading, setIsInlineSearchLoading ] = useState(false);
    const [ isXlineSearchLoading, setIsXlineSearchLoading ] = useState(false);
    const [ isZSliceSearchLoading, setIsZSliceSearchLoading ] = useState(false);

    const [ isInlineVisible, setIsInlineVisible ] = useState(true);
    const [ isXlineVisible, setIsXlineVisible ] = useState(true);
    const [ isZSliceVisible, setIsZSliceVisible ] = useState(true);

    const {
        divisionFactor,
        changingDivisionFactor,
        screenHeightFactor,
        tileWidth,
        skipInlineFactor,
        skipXlineFactor,
        initialDeepeningRange,
        deepeningsTotal,
    } = use3DGridStore(state => ({
        divisionFactor: state.divisionFactor,
        changingDivisionFactor: state.changingDivisionFactor,
        screenHeightFactor: state.screenHeightFactor,
        tileWidth: state.tileWidth,
        skipInlineFactor: state.skipInlineFactor,
        skipXlineFactor: state.skipXlineFactor,
        initialDeepeningRange: state.initialLoadingRange,
        deepeningsTotal: state.totalLoadingIterations,
        totalZsliceNumbers: state.totalZsliceNumbers,
        setSearchedInlineNumber: state.setSearchedInlineNumber,
        setSearchedXlineNumber: state.setSearchedXlineNumber,
        setTotalInlineNumbers: state.setTotalInlineNumbers,
        setTotalXlineNumbers: state.setTotalXlineNumbers,
        setTotalZsliceNumbers: state.setTotalZsliceNumbers,
        setSelectedInlineNumber: state.setSelectedInlineNumber,
        setSelectedXlineNumber: state.setSelectedXlineNumber,
        setSelectedSeismic3DInfo: state.setSelectedSeismic3DInfo,
        setIsXLineSearchLoading: state.setIsXLineSearchLoading,
        setIsInlineSearchLoading: state.setIsInlineSearchLoading,
        setSearchedZsliceNumber: state.setSearchedZsliceNumber
    }), shallow);

    const [ gridLoadingError, setGridLoadingError ] = useState<boolean>(false);

    const survey3DInfo = volumeMetadata?.Survey3DInfo;
    const entitlementLimits = volumeMetadata?.Entitlement3DLimits;

    const [ inlineRelativeNumbersToLoad, setInlineRelativeNumbersToLoad ] = useState<number[]>([]);

    const totalOfDeepeningIterations = useMemo(() => getUrlParams().totalLoadingIterations, []);

    const lineLoadingStepRef = useRef<number>(getUrlParams().lineLoadingStep);

    const featureFlags = use3DFeatureFlags();

    const inlineNumbersToLoad = useMemo(() => {
        if (survey3DInfo && entitlementLimits) {
            return inlineRelativeNumbersToLoad.map(relativeNumber => getRealLineValue(relativeNumber, survey3DInfo.InlineStart, survey3DInfo.InlineIncrement, entitlementLimits.InlineStart - survey3DInfo.InlineStart));
        } else {
            return [];
        }
    }, [entitlementLimits, inlineRelativeNumbersToLoad, survey3DInfo]);

    const [ geometryLoadedInlinesNumbers, setGeometryLoadedInlineNumbers ] = useState<number[]>([]);

    const [ imageLoadedInlinesTotal, setImageLoadedInlinesTotal ] = useState<number>(0);

    const [ inlineNumbersWithError, setInlineNumbersWithError ] = useState<number[]>([]);
    const [ xlineNumbersWithError, setXlineNumbersWithError ] = useState<number[]>([]);

    const [ geometryLoadedXlinesNumbers, setGeometryLoadedXlineNumbers ] = useState<number[]>([]);

    const [ imageLoadedXlinesTotal, setImageLoadedXlinesTotal ] = useState<number>(0);

    const [ imageLoadedZslicesTotal, setImageLoadedZslicesTotal ] = useState(0);

    const [ xlineRelativeNumbersToLoad, setXlineRelativeNumbersToLoad ] = useState<number[]>([]);

    const [ zsliceRelativeNumbersToLoad, setZsliceRelativeNumbersToLoad ] = useState<number[]>([]);

    const { data: volumeOutlineWkt } = useGetSurveyGeometry(volumeMetadata.VolumeToken, volumeIdentifier, mainFeatureSrid, !!volumeMetadata);

    const [ sampleInterval, samplesPerTrace ] = useMemo(() => {
        if (!volumeMetadata) {
            return [null, null];
        } else {
            const sampleIntervalUnit = volumeMetadata.Header.SampleIntervalUnit;
            return [sampleIntervalUnit === SampleIntervalUnit.Milisecond ? volumeMetadata.Header.SampleInterval / 1000 : volumeMetadata.Header.SampleInterval,
                volumeMetadata.Header.SamplesPerTrace];
        }
    }, [volumeMetadata]);

    const colorbar = useMemo(() => {
        if (volumeMetadata.IsVelocity) {
            return 'jet';
        }
        else {
            return 'binary';
        }
    }, [volumeMetadata]);

    const zsliceNumbersToLoad = useMemo(() => {
        if (!sampleInterval) {
            return [];
        } else {
            return zsliceRelativeNumbersToLoad.map(relativeNumber => relativeNumber * sampleInterval);
        }
    }, [sampleInterval, zsliceRelativeNumbersToLoad]);

    const previousTotalZsliceNumbersRef = useRef<number[]>([]);

    const volumeType = useMemo<SurveyType>(() => volumeMetadata.Type ?? SurveyType.Seismic3D, [volumeMetadata]);

    const xlineNumbersToLoad = useMemo(() => {
        if (survey3DInfo && entitlementLimits) {
            return xlineRelativeNumbersToLoad.map(relativeNumber => getRealLineValue(relativeNumber, survey3DInfo.XlineStart, survey3DInfo.XlineIncrement, entitlementLimits.XlineStart - survey3DInfo.XlineStart ));
        } else {
            return [];
        }
    }, [entitlementLimits, survey3DInfo, xlineRelativeNumbersToLoad]);

    const startFirstDeepeningIteration = useCallback(() => {
        if (!entitlementLimits || !featureFlags) {
            return;
        }

        if (volumeType === SurveyType.Seismic3D) {
            const inlineRelativeNumbers = getRelativeLinesToLoadInFirstIteration(initialDeepeningRange, entitlementLimits?.TotalInlines);
            const xlineRelativeNumbers = getRelativeLinesToLoadInFirstIteration(initialDeepeningRange, entitlementLimits?.TotalXlines);
            setInlineRelativeNumbersToLoad(inlineRelativeNumbers);
            setXlineRelativeNumbersToLoad(xlineRelativeNumbers);
        }

        currentDeepeningIterationRef.current = 1;
    }, [entitlementLimits, featureFlags, initialDeepeningRange]);

    useEffect(() => setTotalZSliceNumbers(zsliceNumbersToLoad), [zsliceNumbersToLoad]);

    const increaseDeepeningIteration = useCallback(() => {
        currentDeepeningIterationRef.current += 1;

        if (currentDeepeningIterationRef.current > totalOfDeepeningIterations) {
            return;
        }
        onLoadingProgressChange(0);

        const updatedInlineNumbers = getDeeperRelativeLinesToLoad(inlineRelativeNumbersToLoad);
        const updatedXlineNumbers = getDeeperRelativeLinesToLoad(xlineRelativeNumbersToLoad);
        setInlineRelativeNumbersToLoad(updatedInlineNumbers);
        setXlineRelativeNumbersToLoad(updatedXlineNumbers);

        if (featureFlags?.enableZslice) {
            const updatedZsliceNumbers = getDeeperRelativeLinesToLoad(zsliceRelativeNumbersToLoad);
            setZsliceRelativeNumbersToLoad(updatedZsliceNumbers);
        }
    }, [totalOfDeepeningIterations, inlineRelativeNumbersToLoad, xlineRelativeNumbersToLoad, featureFlags?.enableZslice, zsliceRelativeNumbersToLoad]);

    const handleInlineError = (inlineNumbers: number[]) => setInlineNumbersWithError(current => [...current, ...inlineNumbers]);
    const handleXlineError = (xlineNumbers: number[]) => setXlineNumbersWithError(current => [...current, ...xlineNumbers]);

    useEffect(() => {
        if (volumeMetadata && !!geopostScene && !!featureFlags) {
            setSearchedInlineNumber(null);
            setSearchedXlineNumber(null);
            //onLoadStart();
            geopostScene.addGrid(volumeMetadata.VolumeToken);
            startFirstDeepeningIteration();
        }
    }, [volumeMetadata, geopostScene, featureFlags]);

    useEffect(() => {
        const imageToLoadLinesTotal = inlineNumbersToLoad.length + xlineNumbersToLoad.length + zsliceNumbersToLoad.length;
        const loadedImageLinesTotal = imageLoadedInlinesTotal + imageLoadedXlinesTotal + inlineNumbersWithError.length + xlineNumbersWithError.length + imageLoadedZslicesTotal;
        if (loadedImageLinesTotal !== 0) {
            onLoadingProgressChange(loadedImageLinesTotal/imageToLoadLinesTotal * 100);
        }

        console.log('=============================== LOADING PROGRESS =========================', loadedImageLinesTotal + '/' + imageToLoadLinesTotal, [imageLoadedInlinesTotal, imageLoadedXlinesTotal, imageLoadedZslicesTotal]);
        if (imageToLoadLinesTotal > 0 && loadedImageLinesTotal === (imageToLoadLinesTotal)) {
            increaseDeepeningIteration();
        }
    }, [imageLoadedInlinesTotal, imageLoadedXlinesTotal, inlineNumbersWithError, xlineNumbersWithError, imageLoadedZslicesTotal]);

    useEffect(() => {
        if (geometryLoadedInlinesNumbers.length + inlineNumbersWithError.length === inlineNumbersToLoad.length &&
            geometryLoadedXlinesNumbers.length + xlineNumbersWithError.length === xlineNumbersToLoad.length
            && (geometryLoadedInlinesNumbers.length > 0 || geometryLoadedXlinesNumbers.length > 0)) {
            //onLoadEnd();

            if (currentDeepeningIterationRef.current === 1) {
                selectCentralLines();

                if (featureFlags?.enableZslice && !!samplesPerTrace) {
                    const zsliceRelativeNumbers = getRelativeLinesToLoadInFirstIteration(initialDeepeningRange, samplesPerTrace);
                    setZsliceRelativeNumbersToLoad(zsliceRelativeNumbers);

                }

                onLoadingProgressChange(1);
            }
        }
    }, [geometryLoadedXlinesNumbers, geometryLoadedInlinesNumbers, inlineNumbersWithError, xlineNumbersWithError]);

    useEffect(() => {
        setTotalInlineNumbers(geometryLoadedInlinesNumbers);
        setTotalXlineNumbers(geometryLoadedXlinesNumbers);
        //setTotalZsliceNumbers(zsliceNumbersToLoad);
    }, [geometryLoadedXlinesNumbers, geometryLoadedInlinesNumbers]);

    const selectCentralLines = () => {
        const centralInlineIndex = Math.round(geometryLoadedInlinesNumbers.length / 2);
        const centralInlineNumber = geometryLoadedInlinesNumbers[centralInlineIndex];
        setSearchedInlineNumber(centralInlineNumber);

        const centralXlineIndex = Math.round(geometryLoadedXlinesNumbers.length / 2);
        const centralXlineNumber = geometryLoadedXlinesNumbers[centralXlineIndex];
        setSearchedXlineNumber(centralXlineNumber);
    };

    const [ searched2DLineNumber, setSearched2DLineNumber ] = useState<number|null>(null);

    const { current: lineNumbersToLoad2D } = useRef([0]);

    const handle2DLoadingEnd = useCallback(() => {
        onLoadingProgressChange(100);
        onLoadEnd();
    }, [onLoadEnd, onLoadingProgressChange]);

    useEffect(() => {
        if (currentDeepeningIterationRef.current === 1 && totalZSliceNumbers.length > 0 && previousTotalZsliceNumbersRef.current.length === 0) {
            const centralZsliceIndex = Math.round(zsliceNumbersToLoad.length / 2);
            const centralZsliceNumber = zsliceNumbersToLoad[centralZsliceIndex];
            setSearchedZSliceNumber(centralZsliceNumber);
        }
        previousTotalZsliceNumbersRef.current = totalZSliceNumbers;
    }, [totalZSliceNumbers]);

    useEffect(() => {
        console.log('====================> Recreating grid for ' + volumeMetadata.VolumeToken + ' <========================');
    }, []);

    useEffect(() => {
        if (!!volumeOutlineWkt && !!sampleInterval) {
            const outlineMesh = buildMeshByGeomWKT(volumeOutlineWkt, null, '#0352ff', mainFeatureCentroid as [number, number]);
            outlineMesh.position.setY(outlineMesh.position.y + getThreeJsYFactor(volumeMetadata.Datum/sampleInterval, sampleInterval));
            geopostScene?.add(outlineMesh);
        }
    }, [volumeOutlineWkt]);

    return (
        <React.Fragment>
            {
                isSliderVisible
                &&
                <GridSlider
                    availableInlineNumbers={totalInlineNumbers}
                    availableXlineNumbers={totalXlineNumbers}
                    availableZSlceNumbers={totalZSliceNumbers}
                    inlineNumber={selectedInlineNumber}
                    xlineNumber={selectedXlineNumber}
                    zSliceNumber={selectedZSliceNumber}
                    isInlineLoading={isInlineSearchLoading}
                    isXlineLoading={isXlineSearchLoading}
                    isZSliceLoading={isZSliceSearchLoading}
                    isInlineVisible={isInlineVisible}
                    isXlineVisible={isXlineVisible}
                    isZSliceVisible={isZSliceVisible}
                    onInlineNumberChange={setSearchedInlineNumber}
                    onXlineNumberChange={setSearchedXlineNumber}
                    onZSliceNumberChange={setSearchedZSliceNumber}
                    onInlineVisibilityChange={setIsInlineVisible}
                    onXlineVisibilityChange={setIsXlineVisible}
                    onZSliceVisibilityChange={setIsZSliceVisible}
                    volumeMetadata={volumeMetadata}
                />
            }
            {
                geopostScene
                &&
                <React.Fragment>
                    {
                        (
                            volumeType === SurveyType.Seismic3D ?
                                <React.Fragment>
                                    <Lines3D2
                                        lineLoadingStep={lineLoadingStepRef.current}
                                        seismicMetadata={volumeMetadata}
                                        lineType={LineType.Inline}
                                        scene={geopostScene}
                                        searchedLineNumber={searchedInlineNumber}
                                        srid={mainFeatureSrid}
                                        setSelectedLineNumber={setSelectedInlineNumber}
                                        setIsPriorityLineLoading={setIsInlineSearchLoading}
                                        onTotalImageLoadedLinesChange={setImageLoadedInlinesTotal}
                                        onLoadedLineNumbersChange={setGeometryLoadedInlineNumbers}
                                        lineNumbersToLoad={inlineNumbersToLoad}
                                        onLoadingLineError={handleInlineError}
                                        isVisible={isInlineVisible}
                                        colorbar={colorbar}
                                    />
                                    <Lines3D2
                                        lineLoadingStep={lineLoadingStepRef.current}
                                        seismicMetadata={volumeMetadata}
                                        lineType={LineType.Xline}
                                        scene={geopostScene}
                                        searchedLineNumber={searchedXlineNumber}
                                        srid={mainFeatureSrid}
                                        setSelectedLineNumber={setSelectedXlineNumber}
                                        setIsPriorityLineLoading={setIsXlineSearchLoading}
                                        onTotalImageLoadedLinesChange={setImageLoadedXlinesTotal}
                                        onLoadedLineNumbersChange={setGeometryLoadedXlineNumbers}
                                        lineNumbersToLoad={xlineNumbersToLoad}
                                        onLoadingLineError={handleXlineError}
                                        isVisible={isXlineVisible}
                                        colorbar={colorbar}
                                    />
                                    {
                                        featureFlags?.enableZslice
                                        &&
                                        <Zslice3D
                                            srid={mainFeatureSrid}
                                            isVisible={isZSliceVisible}
                                            surveyMetadata={volumeMetadata}
                                            sliceNumbersToLoad={zsliceNumbersToLoad}
                                            onTotalSlicesLoadedChange={setImageLoadedZslicesTotal}
                                            colorbar={colorbar}
                                            onLoadedSlicesChange={setTotalZSliceNumbers}
                                            searchedZSliceNumber={searchedZSliceNumber}
                                            setIsSearchedZSliceLoading={setIsZSliceSearchLoading}
                                            setSelectedZSliceNumber={setSelectedZSliceNumber}
                                            zsliceNumbersToLoad={zsliceNumbersToLoad}
                                        />
                                    }
                                </React.Fragment>
                                :
                                (
                                    <Lines3D2
                                        lineType={LineType.Line2D}
                                        isVisible={true}
                                        lineLoadingStep={1}
                                        lineNumbersToLoad={lineNumbersToLoad2D}
                                        onLoadedLineNumbersChange={(lineNumbers) => lineNumbers.length > 0 && setSearched2DLineNumber(0)}
                                        onLoadingLineError={() => { } }
                                        onTotalImageLoadedLinesChange={(total) => total > 0 && handle2DLoadingEnd()}
                                        scene={geopostScene}
                                        seismicMetadata={volumeMetadata}
                                        setIsPriorityLineLoading={() => { } }
                                        setSelectedLineNumber={() => { } }
                                        srid={mainFeatureSrid}
                                        searchedLineNumber={searched2DLineNumber}
                                        colorbar={colorbar}
                                    />
                                )
                        )
                    }
                </React.Fragment>
            }
        </React.Fragment>);
};
